import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/permissionsStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * SystemSettingsPage Component
 * 
 * Provides system-level configuration and maintenance operations including:
 * - Cache management (flush and rebuild)
 * - System diagnostics
 * - Performance optimization tools
 */
const SystemSettingsPage = () => {
    const { apiFetch } = useAuth();
    const [isFlushing, setIsFlushing] = useState(false);
    const [cacheStatus, setCacheStatus] = useState(null);
    const [statusInterval, setStatusInterval] = useState(null);

    /**
     * Polls the cache status endpoint to track progress of cache rebuild operations
     */
    const startStatusPolling = () => {
        // Clear any existing interval
        if (statusInterval) {
            clearInterval(statusInterval);
        }

        // Poll every 2 seconds
        const interval = setInterval(async () => {
            try {
                const response = await apiFetch('/api/Settings/cache-status');
                const data = await response.json();
                
                if (data.success) {
                    setCacheStatus(data.data);
                    
                    // Stop polling if not warming up
                    if (!data.data.isWarmingUp) {
                        clearInterval(interval);
                        setStatusInterval(null);
                        setIsFlushing(false);
                    }
                }
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error fetching cache status:', error);
            }
        }, 2000);

        setStatusInterval(interval);
    };

    /**
     * Triggers a full cache flush and rebuild operation
     */
    const handleFlushCaches = async () => {
        try {
            setIsFlushing(true);
            
            const response = await apiFetch('/api/Settings/flush-caches', {
                method: 'POST'
            });
            
            const data = await response.json();
            
            if (data.success) {
                toast.success('Cache flush started successfully');
                startStatusPolling();
            } else {
                toast.error(data.message || 'Failed to flush caches');
                setIsFlushing(false);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error flushing caches:', error);
            toast.error('An error occurred while flushing caches');
            setIsFlushing(false);
        }
    };

    /**
     * Fetches the current cache status
     */
    const handleCheckStatus = async () => {
        try {
            const response = await apiFetch('/api/Settings/cache-status');
            const data = await response.json();
            
            if (data.success) {
                setCacheStatus(data.data);
            } else {
                toast.error('Failed to fetch cache status');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching cache status:', error);
            toast.error('An error occurred while fetching cache status');
        }
    };

    // Clean up polling interval on unmount
    useEffect(() => {
        return () => {
            if (statusInterval) {
                clearInterval(statusInterval);
            }
        };
    }, [statusInterval]);

    return (
        <div className="permissions-management">
            <h1>System Settings</h1>
            <p className="page-description">
                Manage system-level configurations and perform maintenance operations.
            </p>

            {/* Cache Management Section */}
            <div className="settings-section">
                <h2>Cache Management</h2>
                <p className="section-description">
                    The application caches frequently accessed data to improve performance. 
                    Use the button below to flush all caches and rebuild them with fresh data from the database.
                    This operation may take several minutes to complete.
                </p>

                <div className="cache-controls">
                    <button 
                        className="btn btn-primary"
                        onClick={handleFlushCaches}
                        disabled={isFlushing}
                    >
                        {isFlushing ? 'Flushing Caches...' : 'Flush and Rebuild Caches'}
                    </button>

                    <button 
                        className="btn btn-secondary"
                        onClick={handleCheckStatus}
                        disabled={isFlushing}
                        style={{ marginLeft: '10px' }}
                    >
                        Check Status
                    </button>
                </div>

                {/* Cache Status Display */}
                {cacheStatus && (
                    <div className="cache-status" style={{ marginTop: '20px' }}>
                        <h3>Cache Status</h3>
                        <div className="status-grid" style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: '15px',
                            marginTop: '10px'
                        }}>
                            <div className="status-item">
                                <strong>Status:</strong> {cacheStatus.message}
                            </div>
                            <div className="status-item">
                                <strong>Progress:</strong> {cacheStatus.displayCompleted} / {cacheStatus.displayTotal} jobs ({cacheStatus.percent}%)
                            </div>
                            {cacheStatus.startedAt && (
                                <div className="status-item">
                                    <strong>Started At:</strong> {new Date(cacheStatus.startedAt).toLocaleString()}
                                </div>
                            )}
                            {cacheStatus.lastUpdated && (
                                <div className="status-item">
                                    <strong>Last Updated:</strong> {new Date(cacheStatus.lastUpdated).toLocaleString()}
                                </div>
                            )}
                        </div>

                        {/* Progress Bar */}
                        {cacheStatus.isWarmingUp && (
                            <div className="progress-bar-container" style={{ 
                                marginTop: '15px',
                                width: '100%',
                                height: '30px',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '5px',
                                overflow: 'hidden'
                            }}>
                                <div 
                                    className="progress-bar" 
                                    style={{
                                        width: `${cacheStatus.percent}%`,
                                        height: '100%',
                                        backgroundColor: '#4CAF50',
                                        transition: 'width 0.3s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {cacheStatus.percent}%
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Future Sections */}
            <div className="settings-section" style={{ marginTop: '30px' }}>
                <h2>System Information</h2>
                <p className="section-description">
                    Additional system information and diagnostics will be available here in future updates.
                </p>
            </div>
        </div>
    );
};

export default SystemSettingsPage;
